import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CreditCard, Globe2, Loader2, Wallet as WalletIcon } from "lucide-react";
import { startCheckout } from "@/lib/stripe";
import { startPowerTranzCheckout } from "@/lib/powertranz";
import { PayPalButton } from "@/components/PayPalButton";
import { toast } from "sonner";

export type PaymentPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  amountCents: number;
  currency?: "USD" | "BSD";
  featureKey?: string;
  /** Stripe price id (subscription or one-time). Optional — if absent, Stripe option is hidden. */
  stripePriceId?: string;
  stripeMode?: "subscription" | "payment";
  /** PayPal plan id — when set, the PayPal tile renders a subscription button instead of one-time. */
  paypalPlanId?: string;
  /** Hide the PayPal option entirely. Default: shown. */
  disablePaypal?: boolean;
  onSuccess?: (provider: "stripe" | "powertranz" | "paypal") => void;
};

type ActiveMethod = "stripe" | "powertranz" | "paypal" | null;

export const PaymentMethodPicker = ({
  open,
  onOpenChange,
  title = "Choose payment method",
  description,
  amountCents,
  currency = "USD",
  featureKey,
  stripePriceId,
  stripeMode = "payment",
  paypalPlanId,
  disablePaypal = false,
  onSuccess,
}: PaymentPickerProps) => {
  const [loading, setLoading] = useState<ActiveMethod>(null);
  const [paypalOpen, setPaypalOpen] = useState(false);

  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amountCents / 100);

  const payStripe = async () => {
    if (!stripePriceId) return;
    setLoading("stripe");
    try {
      await startCheckout({ priceId: stripePriceId, mode: stripeMode, featureKey });
      onSuccess?.("stripe");
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message || "Stripe checkout failed");
    } finally {
      setLoading(null);
    }
  };

  const payPowerTranz = async () => {
    setLoading("powertranz");
    try {
      await startPowerTranzCheckout({ amountCents, currency, featureKey });
      onSuccess?.("powertranz");
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message || "PowerTranz checkout failed");
    } finally {
      setLoading(null);
    }
  };

  const tileClass =
    "w-full rounded-2xl border border-border bg-card p-4 text-left hover:border-primary transition-colors disabled:opacity-50";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description ?? `Total: ${formatted}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {stripePriceId && (
            <button type="button" onClick={payStripe} disabled={!!loading} className={tileClass}>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-2.5">
                  {loading === "stripe"
                    ? <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    : <Globe2 className="h-5 w-5 text-primary" />}
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-sm">International card</div>
                  <div className="text-xs text-muted-foreground">
                    Visa, Mastercard, Amex — powered by Stripe
                  </div>
                </div>
              </div>
            </button>
          )}

          <button type="button" onClick={payPowerTranz} disabled={!!loading} className={tileClass}>
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-primary/10 p-2.5">
                {loading === "powertranz"
                  ? <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  : <CreditCard className="h-5 w-5 text-primary" />}
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-sm">Bahamian card</div>
                <div className="text-xs text-muted-foreground">
                  RBC, Scotia, CIBC, Commonwealth — powered by PowerTranz
                </div>
              </div>
            </div>
          </button>

          {!disablePaypal && (
            <div className="rounded-2xl border border-border bg-card">
              <button
                type="button"
                onClick={() => setPaypalOpen((o) => !o)}
                disabled={!!loading}
                className="w-full p-4 text-left hover:border-primary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-[#003087]/10 p-2.5">
                    <WalletIcon className="h-5 w-5 text-[#003087] dark:text-[#0070ba]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-bold text-sm">PayPal</div>
                    <div className="text-xs text-muted-foreground">
                      {paypalPlanId
                        ? "Subscribe with your PayPal account"
                        : "Pay with PayPal balance or linked card"}
                    </div>
                  </div>
                </div>
              </button>
              {paypalOpen && (
                <div className="px-4 pb-4">
                  <PayPalButton
                    amountCents={paypalPlanId ? undefined : amountCents}
                    currency={currency}
                    featureKey={featureKey}
                    planId={paypalPlanId}
                    onSuccess={() => {
                      onSuccess?.("paypal");
                      onOpenChange(false);
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground text-center mt-2">
          Payments are processed securely. Card details never touch Cash Isle servers.
        </p>
      </DialogContent>
    </Dialog>
  );
};
